import { Flow, FormBuilderAppUrlMap, FormBuilderAppType, SharedUserProfile } from '~/constants';
import { Locale } from '~/locale/types';
import { Session } from '~/models/auth';
import Profile, { USER_PROFIL_KEY } from '~/models/profile';
import profileAPi from '~/services/profile.service';
import getRuntimeConfig from '~/services/runtime-config.service';
import { PROFILE_TIERS } from '~/constants/profileTier';

const getSharingUserProfile = (
    session: Session | null,
    app: string,
    lang: Locale = 'en',
    profile?: Profile,
    unmaskedHCNum?: string,
    unmaskedDLNum?: string
): SharedUserProfile | undefined => {
    if (!session) return undefined;

    const healthCard =
        profile?.products?.healthCard && unmaskedHCNum
            ? { ...(profile?.products?.healthCard ?? {}), number: unmaskedHCNum }
            : undefined;

    const driverLicence =
        profile?.products?.driverLicense && unmaskedDLNum
            ? { ...(profile?.products?.driverLicense ?? {}), number: unmaskedDLNum }
            : undefined;

    const sharedProfile: SharedUserProfile = {
        userId: session.user?.id,
        firstName: profile?.details?.firstName,
        lastName: profile?.details?.lastName,
        middleName: profile?.details?.middleName,
        email: session.user?.email,
        phoneNumber: profile?.details?.profilePhoneDetails?.phoneNumber,
        extension: profile?.details?.profilePhoneDetails?.extension,
        lang,
        address: profile?.address,
        healthCard: app === Flow.CHC || Flow.ODR ? healthCard : undefined,
        dateOfBirth: app === Flow.ODR ? profile?.details?.dateOfBirth : undefined,
        driverLicence: app === Flow.PLP || app === Flow.GLP ? driverLicence : undefined,
    };
    return sharedProfile;
};

export const shareProfileInSession = (
    session: Session | null,
    app: string,
    lang: Locale = 'en',
    props?: Profile,
    unmaskedHCNum?: string,
    unmaskedDLNum?: string
) => {
    const profile = getSharingUserProfile(session, app, lang, props, unmaskedHCNum, unmaskedDLNum);
    if (profile) {
        sessionStorage.setItem(USER_PROFIL_KEY, JSON.stringify(profile));
        sessionStorage.setItem('authTokens', JSON.stringify(session));
    }
};

export const cleanSharedProfile = () => {
    sessionStorage.removeItem(USER_PROFIL_KEY);
    sessionStorage.removeItem('authTokens');
};

const MAX_REDIRECT_URI_LENGTH = 2048;

const hasControlChars = (value: string) => /[\u0000-\u001F\u007F]/.test(value);

export const isHttpAbsoluteUrl = (value?: string | null) => /^https?:\/\//i.test(value ?? '');

export const isSafeRelativeCxpPath = (value?: string | null) => {
    if (!value) {
        return false;
    }

    if (!value.startsWith('/') || value.startsWith('//')) {
        return false;
    }

    return !hasControlChars(value);
};

export const sanitizeRedirectUriInput = (value?: string | null) => {
    if (!value) {
        return undefined;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_REDIRECT_URI_LENGTH) {
        return undefined;
    }

    if (trimmed.startsWith('//') || hasControlChars(trimmed)) {
        return undefined;
    }

    if (/^(javascript|data):/i.test(trimmed)) {
        return undefined;
    }

    if (trimmed.startsWith('/') || isHttpAbsoluteUrl(trimmed)) {
        return trimmed;
    }

    return undefined;
};

/**
 * A caller-supplied return url reduced to a root-relative path on this origin, or undefined.
 *
 * form-ui names the parameter `redirectUri` when its return url is root-relative and
 * `redirectUrl` when it is absolute - which of the two it sends depends on the runtime mode it
 * was deployed in (see buildCxpSignInUrl / buildPublicSecureReturnUrl there) - so both spellings
 * have to be accepted. An absolute url is kept only when it points back at this origin, and is
 * reduced to a path so that callers only ever deal with one already-validated shape.
 */
export const resolveSameOriginReturnPath = (value?: string | string[]): string | undefined => {
    const raw = Array.isArray(value) ? value[0] : value;
    const sanitized = sanitizeRedirectUriInput(raw);
    if (!sanitized) {
        return undefined;
    }

    if (!isHttpAbsoluteUrl(sanitized)) {
        // sanitizeRedirectUriInput has already rejected '//', control characters and
        // javascript:/data:, so what is left is root-relative on this origin.
        return sanitized;
    }

    if (typeof window === 'undefined') {
        return undefined;
    }

    try {
        const target = new URL(sanitized);
        if (target.origin !== window.location.origin) {
            return undefined;
        }

        return `${target.pathname}${target.search}${target.hash}`;
    } catch {
        return undefined;
    }
};

export const isSsoEnabledForFlow = async (flow?: string): Promise<boolean> => {
    if (!flow) {
        return false;
    }

    let ssoEnabledAppsConfig: string;
    if (typeof window === 'undefined') {
        ssoEnabledAppsConfig = process.env.CXP_SSO_ENABLED_APPS ?? '';
    } else {
        const config = await getRuntimeConfig();
        ssoEnabledAppsConfig = config.ssoEnabledApps ?? '';
    }

    // Callers disagree on casing - the signin page passes the raw `flow` query param, the
    // sso-transfer page passes the raw path segment and getDestinationUrlForFlow passes the
    // uppercased intendedFlow - so compare case-insensitively rather than requiring the
    // configured value to match one caller and silently fail the others.
    const ssoEnabledApps = ssoEnabledAppsConfig
        .split(',')
        .map(app => app.trim().toUpperCase())
        .filter(Boolean);
    return ssoEnabledApps.includes(flow.trim().toUpperCase());
};

export const normalizeDraftRedirectUri = (redirectUri?: string) => {
    if (!redirectUri || !redirectUri.startsWith('/')) {
        return redirectUri;
    }

    const match = redirectUri.match(/^([^?#]*)(.*)$/);
    const rawPath = match?.[1] ?? redirectUri;
    const suffix = match?.[2] ?? '';
    const configuredBasePath = `/${(process.env.CXP_APP_BASE_PATH ?? '')
        .split('/')
        .filter(Boolean)
        .join('/')}`;

    let normalizedPath = rawPath;
    if (configuredBasePath !== '/') {
        while (
            normalizedPath === configuredBasePath ||
            normalizedPath.startsWith(`${configuredBasePath}/`)
        ) {
            normalizedPath = normalizedPath.substring(configuredBasePath.length) || '/';
        }
    }

    while (normalizedPath === '/web' || normalizedPath.startsWith('/web/')) {
        normalizedPath = normalizedPath.substring('/web'.length) || '/';
    }

    normalizedPath = normalizedPath.replace(/^\/(en|fr)(?=\/drafts(?:\/|$))/, '');

    if (!/^\/drafts(?:\/|$)/.test(normalizedPath)) {
        return redirectUri;
    }

    return `${normalizedPath}${suffix}`;
};

export const getSatelliteAppUrl = (host: string, endpoint: string, lang: Locale = 'en'): string => {
    if (!host || !endpoint) return '';
    const withProtocol = /^https?:\/\//i.test(host) ? host : `https://${host}`;
    // Strip /web base path to avoid /web in satellite app URLs
    const cleanHost = withProtocol.replace(/\/web(\/.*)?$/i, '');
    return `${cleanHost}${endpoint}/${lang}`;
};

export const getFormBuilderAppUrl = (
    app: FormBuilderAppType,
    host: string,
    lang: Locale = 'en'
) => {
    if (!host || !FormBuilderAppUrlMap[app]) return '';

    const ensureProtocol = (value: string) => {
        if (/^https?:\/\//i.test(value)) {
            return value;
        }
        return `https://${value}`;
    };

    const normalizeHostForApp = (value: string, appType: FormBuilderAppType) => {
        const withProtocol = ensureProtocol(value);

        // Form builder apps live at the root host, not under /web
        const stripWeb = [
            Flow.APP,
            Flow.CHC,
            Flow.ODR,
            Flow.GLP,
            Flow.PLP,
            Flow.EHOCM,
            Flow.VECON,
        ] as FormBuilderAppType[];
        if (stripWeb.includes(appType)) {
            try {
                const url = new URL(withProtocol);
                return url.origin;
            } catch {
                return withProtocol.replace(/\/web(\/.*)?$/i, '').replace(/\/+$/g, '');
            }
        }
        return withProtocol;
    };

    const path =
        lang === 'fr'
            ? FormBuilderAppUrlMap[`${app}_fr` as FormBuilderAppType]
            : FormBuilderAppUrlMap[app];

    const rawHost = host.trim().replace(/\s+/g, '');
    const normalizedHost = normalizeHostForApp(rawHost, app);
    const normalizedPath = path?.startsWith('/') ? path : `/${path}`;
    return `${normalizedHost.replace(/\/+$/g, '')}${normalizedPath}`.replace(/([^:]\/)\/+/g, '$1');
};

export const displayIntegratedApp = (app: string, env: string) => {
    if (!app || !env) return false;

    const devApps = (process.env.DEV_EXTERNAL_APPS ?? '').split(',').map(devapp => devapp.trim());
    const isProd = ['production'].includes(env);
    const isDev = devApps.includes(app);
    const appActive = checkAppActive(app);
    return !isDev || !isProd || (isProd && appActive && !isDev);
};

const checkAppActive = (app: string) => {
    const appActiveDates = (process.env.ACTIVE_EXTERNAL_APPS_DATE ?? '')
        .split(',')
        .filter(appDate => appDate.toLowerCase().startsWith(app.toLowerCase()))
        .filter(Boolean)
        .map(date => {
            const effectiveDate = date.trim().split(':');
            return effectiveDate.length > 1 ? effectiveDate[1]?.trim() : '';
        });

    if (appActiveDates.length === 0) return true;

    const currentDate = getCurrentDateWithoutTime();
    const [year, month, day] = appActiveDates[0]?.split('-');
    const y = parseInt(year || '');
    const m = parseInt(month || '');
    const d = parseInt(day || '');
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        const local = new Date(y, m - 1, d);
        if (isNaN(local.getTime())) return true;
        if (local.getTime() < currentDate.getTime()) return true;
        return false;
    }
    return true;
};

export const getDestinationUrlForFlow = async (
    intendedFlow?: string,
    lang: Locale = 'en',
    profileTier?: string
) => {
    let ret = '/dashboard';
    const config = await getRuntimeConfig();

    // dynamic redirect when it is an SSO enabled app
    if (intendedFlow) {
        const isSsoEnabledFlow = await isSsoEnabledForFlow(intendedFlow);
        if (isSsoEnabledFlow) {
            return `/auth/sso-transfer/${intendedFlow}`;
        }
    }

    switch (intendedFlow) {
        case Flow.IAC:
            ret = '/address-change';
            break;
        case Flow.DLR:
            ret = '/product-renewal/renewal-options/DL';
            break;
        case Flow.HCR:
            ret = '/product-renewal/renewal-options/HC';
            break;
        case Flow.OPCR:
            ret = '/product-renewal/renewal-options/OPC';
            break;
        case Flow.LPR:
            ret = '/licence-plate-renewal';
            break;
        case Flow.DLV:
            ret =
                profileTier === PROFILE_TIERS.T2
                    ? '/product-renewal/contact-details'
                    : '/verify-product?productType=DL';
            break;
        case Flow.OPCV:
            ret =
                profileTier === PROFILE_TIERS.T2
                    ? '/product-renewal/contact-details'
                    : '/verify-product?productType=OPC';
            break;
        case Flow.HCV:
            ret =
                profileTier === PROFILE_TIERS.T2
                    ? '/product-renewal/contact-details'
                    : '/verify-product?productType=HC';
            break;
        case Flow.DLHCV:
            ret =
                profileTier === PROFILE_TIERS.T2
                    ? '/product-renewal/contact-details'
                    : '/verify-product?productType=DLHC';
            break;
        case Flow.IACV:
            ret =
                profileTier === PROFILE_TIERS.T2
                    ? '/address-change/select-product'
                    : '/verify-product?productType=IAC';
            break;
        case Flow.PF:
            ret = '/pay-fine';
            break;
        case Flow.DR3:
            ret = '/driver-record-3-year';
            break;
        case Flow.VA:
            ret = '/vehicle-abstract-uncertified';
            break;

        /* Form Builder Apps */
        case Flow.CHC:
        case Flow.OTRIS:
        case Flow.OER:
        case Flow.CHC_TRACK:
        case Flow.GLP:
        case Flow.PLP:
        case Flow.PUB_SGR:
        case Flow.ODR:
        case Flow.EHOCM:
        case Flow.VECON:
        case Flow.APP:
        case Flow.OER:
            ret = getFormBuilderAppUrl(
                intendedFlow as FormBuilderAppType,
                config.cxpWebHost as string,
                lang
            );
            break;
        /* End of Form Builder Apps */

        case Flow.EAB:
            ret = (await getRuntimeConfig()).eabAppUrl || '/appointment-booking' + `/${lang}`;
            break;
        case Flow.SP:
            ret = getSatelliteAppUrl(
                config.cxpWebHost as string,
                process.env.SP_ENDPOINT || '',
                lang
            );
            break;
        case Flow.HSO: {
            const hsoHost =
                config.cxpWebHost || (typeof window !== 'undefined' ? window.location.origin : '');
            ret = getSatelliteAppUrl(hsoHost, process.env.HSO_ENDPOINT || '/hso-ui', lang);
            break;
        }
        default:
            break;
    }

    return ret;
};

export const prepareIntegratedAppData = async (
    intendedFlow: keyof typeof Flow,
    session: Session,
    locale: Locale,
    customer?: Profile
) => {
    let healthCard = undefined;

    try {
        if (intendedFlow === Flow.CHC) {
            const orgHealthCard = await profileAPi.getOriginalHealthCard();
            healthCard = orgHealthCard?.data;
        }
    } catch (error) {
        console.error('Error fetching health card:', error);
    }

    let profileData = customer;
    try {
        if (!customer?.details?.firstName || !customer?.details?.lastName) {
            const profile = await profileAPi.getProfile();
            profileData = profile?.data;
        }
    } catch (error) {
        console.error('Error fetching profile data:', error);
    }

    shareProfileInSession(session, intendedFlow, locale, profileData, healthCard?.number);
};

export const getCurrentDateWithoutTime = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};
